import { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalProps, Modal, ModalHeader, ModalFooter, ModalTitle, FormSelect, SelectOption, ModalCloseButton, NotificationDispatch } from '@tryhackme/thm-ui-components';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTrackEventMutation } from 'src/common/slices';
import { SegmentShareEvent } from 'src/common/types';
import { ReactComponent as TwitterXIcon } from 'src/images/svgs/socials/x-twitter.svg';
import { ReactComponent as FacebookIcon } from 'src/images/svgs/socials/facebook.svg';
import { ReactComponent as LinkedinIcon } from 'src/images/svgs/socials/linkedin.svg';
import { useGenerateBadgeImageMutation, useGetUserBadgesQuery } from 'src/features/public-profile/public-profile.slice';
import { useGetBadgesQuery } from 'src/features/badges/badges.slice';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { Achievement } from 'src/common/page-share';
import { StyledBadgeImage, StyledFormTitle, StyledShareSocialButtonsWrapper, StyledSocialButton, StyledTitle } from './share-room-badge-modal.styles';
export const ShareRoomBadgeModal = ({
  open,
  onOpenChange
}: ModalProps) => {
  const {
    username
  } = useParams();
  const [selectedBadge, setSelectedBadge] = useState<SelectOption>();
  const {
    data: badgesData
  } = useGetBadgesQuery();
  const {
    data: userBadgeData
  } = useGetUserBadgesQuery({
    username: username ?? ''
  }, {
    skip: !username
  });
  const [generateBadgeImage, {
    isLoading: isGeneratingBadgeImage,
    data: badgeImageData
  }] = useGenerateBadgeImageMutation();
  const [trackEvent] = useTrackEventMutation();
  const badgeOptions = useMemo(() => userBadgeData?.data?.map(badge => ({
    value: badge.name,
    label: badge.name
  })) || [], [userBadgeData]);
  const selectedBadgeData = badgesData?.data?.badges?.find(badge => badge.name === selectedBadge?.value);
  const [searchParams, setSearchParams] = useSearchParams();
  const showAchievementBadge = searchParams.get('show_achievement_badge'); // Does the user want to see a badge?

  useEffect(() => {
    if (showAchievementBadge !== null && badgeOptions.length) {
      const paramSelectedBadge = badgeOptions.find(badge => badge.value === showAchievementBadge);
      onOpenChange?.(true);
      setSelectedBadge(paramSelectedBadge?.value ? paramSelectedBadge : badgeOptions[0]);
      setSearchParams({});
    }
  }, [badgeOptions, onOpenChange, searchParams, setSearchParams, showAchievementBadge]);

  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const badgeUrl = `https://tryhackme.com/r/${username}/badges/${selectedBadgeData?.name}`;

  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const shareDescription = `I've earned the ${selectedBadgeData?.title} Badge on TryHackMe for ${selectedBadgeData?.description}`;
  useEffect(() => {
    const handleGenerateBadgeImage = async (badgeName: string) => {
      try {
        await generateBadgeImage({
          badgeName
        }).unwrap();
      } catch (error) {
        NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'Something went wrong.');
      }
    };
    if (selectedBadge?.value) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleGenerateBadgeImage(selectedBadge.value);
    }
  }, [generateBadgeImage, selectedBadge?.value]);
  const trackShareClick = useCallback((event: SegmentShareEvent) => {
    if (selectedBadge) {
      // eslint-disable-next-line no-void
      void trackEvent({
        event: 'social-share',
        properties: {
          achievement: Achievement.badge,
          page: 'public-profile',
          medium: event.split('-').slice(1).join('-'),
          name: selectedBadge.label
        }
      });
    }
  }, [selectedBadge, trackEvent]);
  return <Modal open={open} onOpenChange={onOpenChange} overflowVisible maxHeight="none" data-sentry-element="Modal" data-sentry-component="ShareRoomBadgeModal" data-sentry-source-file="share-room-badge-modal.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="share-room-badge-modal.tsx">
        <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="share-room-badge-modal.tsx">Share your badge achievements</ModalTitle>
      </ModalHeader>
      <StyledFormTitle data-sentry-element="StyledFormTitle" data-sentry-source-file="share-room-badge-modal.tsx">Select badge to share</StyledFormTitle>
      <FormSelect selected={selectedBadge} handleSelection={setSelectedBadge} options={badgeOptions} data-sentry-element="FormSelect" data-sentry-source-file="share-room-badge-modal.tsx" />

      {isGeneratingBadgeImage && <StyledTitle>Generating badge image...</StyledTitle>}

      {badgeImageData?.data?.image && <>
          <StyledTitle leftAligned>Your badge</StyledTitle>
          <StyledBadgeImage src={badgeImageData?.data?.imageUrl ?? ''} alt="Badge" />

          <StyledTitle>Share on social media</StyledTitle>
          <StyledShareSocialButtonsWrapper>
            <StyledSocialButton data-social="twitter-x" data-testid="share-twitter-x" target="_blank" href={`https://twitter.com/intent/tweet?url=${badgeUrl}&via=realtryhackme&text=${shareDescription}&hashtags=tryhackme`} onClick={() => trackShareClick('share-twitter')}>
              <TwitterXIcon />
            </StyledSocialButton>
            <StyledSocialButton data-social="linkedin" data-testid="share-linkedin" target="_blank" href={`https://www.linkedin.com/sharing/share-offsite/?url=${badgeUrl}`} onClick={() => trackShareClick('share-linkedin')}>
              <LinkedinIcon />
            </StyledSocialButton>
            <StyledSocialButton data-testid="share-facebook" data-social="facebook" target="_blank" href={`https://www.facebook.com/sharer/sharer.php?u=${badgeUrl}&t=${shareDescription}`} onClick={() => trackShareClick('share-facebook')}>
              <FacebookIcon />
            </StyledSocialButton>
          </StyledShareSocialButtonsWrapper>
        </>}
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="share-room-badge-modal.tsx">
        <ModalCloseButton text="Close" handleCloseButtonAction={() => onOpenChange?.(false)} data-sentry-element="ModalCloseButton" data-sentry-source-file="share-room-badge-modal.tsx" />
      </ModalFooter>
    </Modal>;
};